import { forwardRef } from 'react';

import { cn } from 'components/utils';

import { contentPanelRecipe } from './ContentPanel.recipe';

import type { ContentPanelProps } from './types';

/**
 * Design System 2.0 content panel — a bordered box for grouping non-field content.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer
 * in `components/styles/design-system.css`, so the same markup is correct in both themes and none
 * of the three `.ds-panel*` classes needs a dark rule to achieve it.
 *
 * ── What it is, and what it is not ────────────────────────────────────────────────────────────
 *
 * It is a fill, a rim, a radius and an inset. It is not a card with a header, not a section with
 * a title, and not a region: it renders a plain `<div>` with no role, so it adds nothing to the
 * accessibility tree and cannot accidentally announce a landmark that has no name.
 *
 * A caller who wants it to BE a landmark says so — `role="region"` with an `aria-label` or an
 * `aria-labelledby` pointing at the heading inside — and that is the right place for the decision
 * rather than a default here. A region with no accessible name is not exposed as one at all, so a
 * built-in `role="region"` would be silently inert on most panels and a surprise landmark on the
 * few that happened to be labelled.
 *
 * Nothing here is interactive. No hover, no focus, no disabled state, no tab stop of its own; the
 * only focusable things inside a panel are the ones a caller put there.
 *
 * ── No Base UI primitive behind it ────────────────────────────────────────────────────────────
 *
 * `Tag` is the precedent: a styled container with no headless base, because there is no behaviour
 * to keep off the invented list. A panel has no open state, no selection, no keyboard model and
 * no focus to manage, so wrapping one in a headless primitive would add a dependency and an extra
 * element in exchange for nothing.
 *
 * ── Layout belongs to the caller ──────────────────────────────────────────────────────────────
 *
 * The panel does not stack, space or align its children — see `contentPanelRecipe` for why. A
 * caller who wants a column composes one inside, where they can see it.
 */
export const ContentPanel = forwardRef<HTMLDivElement, ContentPanelProps>(
  ({ children, className, surface, ...rest }, ref) => (
    <div ref={ref} className={cn(contentPanelRecipe({ surface }), className)} {...rest}>
      {children}
    </div>
  )
);
